import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import AppShell from "@/layout/AppShell";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import MyProfile from "@/pages/MyProfile";
import Dashboard from "@/pages/Dashboard";
import ServiceUsers from "@/pages/ServiceUsers";
import ServiceUserDetail from "@/pages/ServiceUserDetail";
import Visits from "@/pages/Visits";
import Incidents from "@/pages/Incidents";
import Safeguarding, { RaiseConcernPage } from "@/pages/Safeguarding";
import Complaints from "@/pages/Complaints";
import RiskAssessments from "@/pages/RiskAssessments";
import Staff from "@/pages/Staff";
import Rota from "@/pages/Rota";
import FamilyPortal from "@/pages/FamilyPortal";
import Reports from "@/pages/Reports";
import MfaSettings from "@/pages/MfaSettings";
import DbsDashboard from "@/pages/DbsDashboard";
import Integrations from "@/pages/Integrations";
import LeaveRequests from "@/pages/LeaveRequests";
import CareTeamInbox from "@/pages/CareTeamInbox";
import ChangePassword from "@/pages/ChangePassword";
import JibbleDashboard from "@/pages/JibbleDashboard";
import "@/App.css";

function ProtectedRoute({ children, allowed, allowForcedFlow = false }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  }
  if (user === false) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  // Global gate: force password change before anything else
  if (!allowForcedFlow && user.force_password_change && location.pathname !== "/settings/password-change") {
    return <Navigate to="/settings/password-change" replace />;
  }
  if (allowed && !allowed.includes(user.role) && user.role !== "super_admin") {
    return <Navigate to={landingFor(user.role)} replace />;
  }
  return children;
}

function landingFor(role) {
  if (role === "family_member") return "/family";
  if (role === "care_worker" || role === "bank_worker") return "/visits";
  return "/dashboard";
}

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  return <Navigate to={landingFor(user.role)} replace />;
}

function ShellRoute({ title, allowed, element }) {
  return (
    <ProtectedRoute allowed={allowed}>
      <AppShellWithTitle title={title}>{element}</AppShellWithTitle>
    </ProtectedRoute>
  );
}

function AppShellWithTitle({ title, children }) {
  // pass children via Outlet pattern by wrapping AppShell with custom element
  return (
    <div>
      <AppShellInline title={title} child={children} />
    </div>
  );
}

import { useState } from "react";
import Sidebar from "@/layout/Sidebar";
import Topbar from "@/layout/Topbar";
import { ShieldAlert } from "lucide-react";

function AppShellInline({ title, child }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const showMfaBanner = user && user.mfa_setup_required;
  return (
    <div className="min-h-screen bg-[#F0FAFA]">
      <Sidebar open={open} onClose={() => setOpen(false)} />
      <Topbar title={title} onMenuClick={() => setOpen(true)} />
      <main className="md:ml-64 px-4 py-4 md:px-10 md:py-8 print-area">
        <div className="max-w-[1400px] animate-fade-in space-y-4">
          {showMfaBanner && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900 flex items-center gap-3 no-print" data-testid="mfa-enrollment-banner">
              <ShieldAlert className="h-5 w-5 flex-shrink-0" />
              <p className="flex-1">Two-factor authentication is required for management roles. Please enrol now to keep your account secure.</p>
              <a href="/settings/mfa" className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-3 py-1.5 rounded-md whitespace-nowrap" data-testid="mfa-banner-cta">Enrol now</a>
            </div>
          )}
          {child}
        </div>
      </main>
    </div>
  );
}

const MGMT = ["registered_manager", "deputy_manager", "compliance_manager", "nominated_individual", "administrator", "super_admin"];
const STAFF_ALL = ["registered_manager", "deputy_manager", "compliance_manager", "nominated_individual", "team_leader", "care_worker", "bank_worker", "administrator", "super_admin"];
const ALL = [...STAFF_ALL, "family_member"];
const VISIT_ROLES = ["registered_manager", "deputy_manager", "compliance_manager", "nominated_individual", "team_leader", "care_worker", "bank_worker", "administrator", "super_admin"];
const ROTA_ROLES = ["registered_manager", "deputy_manager", "compliance_manager", "nominated_individual", "team_leader", "administrator", "care_worker", "bank_worker", "super_admin"];

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route path="/dashboard" element={<ShellRoute title="Compliance dashboard" allowed={MGMT} element={<Dashboard />} />} />
      <Route path="/service-users" element={<ShellRoute title="Service users" allowed={ALL} element={<ServiceUsers />} />} />
      <Route path="/service-users/:id" element={<ShellRoute title="Service user record" allowed={ALL} element={<ServiceUserDetail />} />} />
      <Route path="/visits" element={<ShellRoute title="Visits" allowed={VISIT_ROLES} element={<Visits />} />} />
      <Route path="/incidents" element={<ShellRoute title="Incidents" allowed={ALL} element={<Incidents />} />} />
      <Route path="/safeguarding" element={<ShellRoute title="Safeguarding" allowed={STAFF_ALL} element={<Safeguarding />} />} />
      <Route path="/safeguarding/new" element={<ShellRoute title="Raise safeguarding concern" allowed={STAFF_ALL} element={<RaiseConcernPage />} />} />
      <Route path="/complaints" element={<ShellRoute title="Complaints" allowed={ALL} element={<Complaints />} />} />
      <Route path="/risk-assessments" element={<ShellRoute title="Risk assessments" allowed={STAFF_ALL} element={<RiskAssessments />} />} />
      <Route path="/staff" element={<ShellRoute title="Staff & recruitment" allowed={MGMT} element={<Staff />} />} />
      <Route path="/rota" element={<ShellRoute title="Rota" allowed={ROTA_ROLES} element={<Rota />} />} />
      <Route path="/family" element={<ShellRoute title="Family portal" allowed={["family_member"]} element={<FamilyPortal />} />} />
      <Route path="/reports" element={<ShellRoute title="Reports" allowed={MGMT} element={<Reports />} />} />
      <Route path="/dbs" element={<ShellRoute title="DBS compliance" allowed={MGMT} element={<DbsDashboard />} />} />
      <Route path="/leave" element={<ShellRoute title="Leave requests" allowed={STAFF_ALL} element={<LeaveRequests />} />} />
      <Route path="/my-profile" element={<ShellRoute title="My profile" allowed={ALL} element={<MyProfile />} />} />
      <Route path="/inbox" element={<ShellRoute title="Care team inbox" allowed={["registered_manager", "deputy_manager", "compliance_manager", "nominated_individual", "team_leader", "care_worker", "bank_worker", "administrator", "super_admin"]} element={<CareTeamInbox />} />} />
      <Route path="/jibble" element={<ShellRoute title="Time & attendance" allowed={MGMT} element={<JibbleDashboard />} />} />
      <Route path="/integrations" element={<ShellRoute title="Integrations" allowed={MGMT} element={<Integrations />} />} />
      <Route path="/settings/mfa" element={<ShellRoute title="Two-factor authentication" allowed={STAFF_ALL} element={<MfaSettings />} />} />
      <Route path="/settings/password-change" element={<ProtectedRoute allowForcedFlow={true}><AppShellInline title="Set new password" child={<ChangePassword />} /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
